import { useState } from "react";

import type { ApiClient } from "../../shared/api/client";
import type { SectionDraft } from "../../shared/api/generated/contracts";
import { useT } from "../../shared/i18n";
import { Button, Sheet } from "../../shared/ui";
import { KeywordsSheet } from "./KeywordsSheet";
import { VersionHistorySheet } from "./VersionHistorySheet";

const EXAMPLES = [
  "sections.edit.example1",
  "sections.edit.example2",
  "sections.edit.example3",
] as const;

/**
 * How to change a published section: the assistant in the chat makes the
 * edit as a Draft, the Mini App reviews it. Typing edit requests inside the
 * Mini App needs its own job channel and is out of scope.
 */
export function EditSectionSheet({
  open,
  onOpenChange,
  sectionName,
  draft,
  onBackToChat,
  onOpenDraft,
  history,
  keywords,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sectionName: string;
  /** The section's Draft from the bootstrap, when there is one. */
  draft: SectionDraft | null | undefined;
  onBackToChat: () => void;
  onOpenDraft: () => void;
  /** Version history and rollback (AGE-018); absent hides the entry. */
  history?: {
    api: Pick<ApiClient, "listVersions" | "draftFromVersion">;
    abilityId: string;
    /** The restoring Draft exists: open its review. */
    onDraftReady: () => void;
  };
  /** Keywords the assistant routes by (ADR 0236); absent hides the entry. */
  keywords?: {
    api: Pick<ApiClient, "routingProfile" | "editRoutingTerms">;
    abilityId: string;
    /** The Draft that holds the keyword changes: open its review. */
    onDraftReady: () => void;
  };
}) {
  const t = useT();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [keywordsOpen, setKeywordsOpen] = useState(false);
  return (
    <>
      <Sheet
        open={open && !historyOpen && !keywordsOpen}
        onOpenChange={onOpenChange}
        title={`${t("sections.edit")} «${sectionName}»`}
        description={t("sections.edit.intro")}
        footer={
          <>
            {keywords && (
              <Button
                variant="secondary"
                onClick={() => {
                  setKeywordsOpen(true);
                }}
              >
                {t("keywords.open")}
              </Button>
            )}
            {history && (
              <Button
                variant="secondary"
                onClick={() => {
                  setHistoryOpen(true);
                }}
              >
                {t("history.open")}
              </Button>
            )}
            {draft && (
              <Button variant="secondary" onClick={onOpenDraft}>
                {t("sections.edit.draft")}
              </Button>
            )}
            <Button onClick={onBackToChat}>{t("sections.edit.chat")}</Button>
          </>
        }
      >
        {draft && (
          <p
            role="status"
            className={`mb-3 text-body ${draft.stale ? "text-warning" : "text-fg-muted"}`}
          >
            {draft.stale
              ? t("sections.edit.draftStale")
              : t("sections.edit.draftReady")}
          </p>
        )}
        <p className="text-caption font-bold uppercase tracking-wide text-fg-muted">
          {t("sections.edit.examples")}
        </p>
        <ul className="mt-2 flex flex-col gap-2">
          {EXAMPLES.map((key) => (
            <li
              key={key}
              className="rounded-md border border-line-subtle bg-canvas px-3 py-2 text-body"
            >
              «{t(key)}»
            </li>
          ))}
        </ul>
      </Sheet>
      {keywords && (
        <KeywordsSheet
          open={open && keywordsOpen}
          onOpenChange={setKeywordsOpen}
          api={keywords.api}
          abilityId={keywords.abilityId}
          sectionName={sectionName}
          onDraftReady={() => {
            setKeywordsOpen(false);
            keywords.onDraftReady();
          }}
        />
      )}
      {history && (
        <VersionHistorySheet
          open={open && historyOpen}
          onOpenChange={setHistoryOpen}
          api={history.api}
          abilityId={history.abilityId}
          sectionName={sectionName}
          onDraftReady={() => {
            setHistoryOpen(false);
            history.onDraftReady();
          }}
          onOpenExistingDraft={() => {
            setHistoryOpen(false);
            onOpenDraft();
          }}
        />
      )}
    </>
  );
}
